// src/features/tasks/components/TaskDetailsPage/SignCompletionReceiptDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  HowToReg as HowToRegIcon,
  Dialpad,
  Lock,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ProfilePicture from '../../../../components/common/ProfilePicture';
import PinKeypad from '../../../../components/common/PinKeypad';

const PIN_LOCKED_CODES = [
  'COMPLETION_RECEIPT__PIN_LOCKED',
  'COMPLETION_RECEIPT__PIN_NOT_AVAILABLE',
];

export default function SignCompletionReceiptDialog({
  open,
  onClose,
  onSubmit,
  loading = false,
  currentUser,
  staffMembers = [],
  signaturePosition,
  excludeStaffIds = [],
}) {
  const { t } = useTranslation(['tasks', 'common']);

  const availableStaff = staffMembers.filter(
    staff => !excludeStaffIds.includes(staff.staff_member_id)
  );

  // Default to the logged-in user; if they've already signed, leave it empty so
  // the next signer picks themselves (a guessed name invites signing as someone else)
  const defaultStaff = excludeStaffIds.includes(currentUser.staff_member_id)
    ? null
    : currentUser;

  const [selectedStaff, setSelectedStaff] = useState(defaultStaff);
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [notes, setNotes] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('password');

  // Pick the default signer each time the dialog opens: it stays mounted with
  // the page, so a default chosen at mount would predate the receipt loading
  // and could pre-select someone who has already signed.
  useEffect(() => {
    if (open) setSelectedStaff(defaultStaff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, excludeStaffIds.join(',')]);

  // The logged-in user object (from the session) has no pin_enabled, so look
  // the person up in the staff list, which does
  const selectedRecord = staffMembers.find((s) => s.staff_member_id === selectedStaff?.staff_member_id) ?? selectedStaff;
  const pinAvailable = !!selectedRecord?.pin_enabled;

  // Switch to PIN by default when available, fall back to password when not
  useEffect(() => {
    setMode(pinAvailable ? 'pin' : 'password');
    setPin('');
    setPassword('');
    setError('');
  }, [selectedStaff?.staff_member_id, pinAvailable]);

  const handleModeChange = (_event, newMode) => {
    if (!newMode || newMode === mode) return;
    setMode(newMode);
    setError('');
  };

  const handleSubmit = async () => {
    if (!selectedStaff) {
      setError(t('tasks:completion_receipt.staff_member_required', { defaultValue: 'Please select a staff member' }));
      return;
    }

    if (mode === 'pin') {
      if (!/^\d{4}$/.test(pin)) {
        setError(t('tasks:completion_receipt.pin_required', { defaultValue: 'Please enter your 4-digit PIN' }));
        return;
      }

      setError('');
      const result = await onSubmit({ pin, notes, staffMemberId: selectedStaff.staff_member_id });

      if (result.success) {
        resetForm();
      } else if (result.error) {
        setPin('');
        if (result.errorCode && PIN_LOCKED_CODES.includes(result.errorCode)) {
          setMode('password');
        }
        setError(result.error);
      }
      return;
    }

    if (!password) {
      setError(t('tasks:completion_receipt.password_required', { defaultValue: 'Password is required' }));
      return;
    }

    setError('');
    const result = await onSubmit({ password, notes, staffMemberId: selectedStaff.staff_member_id });

    if (result.success) {
      resetForm();
    } else if (result.error) {
      setError(result.error);
    }
  };

  const resetForm = () => {
    setSelectedStaff(defaultStaff);
    setPassword('');
    setPin('');
    setNotes('');
    setShowPassword(false);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              borderRadius: 2,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
            }}
          >
            <HowToRegIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {t('tasks:completion_receipt.sign_completion', { defaultValue: 'Sign Completion Receipt' })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {signaturePosition === 'first'
                ? t('tasks:completion_receipt.first_signature_help', { defaultValue: 'Add your signature as the first verifier' })
                : t('tasks:completion_receipt.second_signature_help', { defaultValue: 'Add your signature as the second verifier' })
              }
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} pt={2}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Autocomplete
            options={availableStaff}
            getOptionLabel={(option) => option.name || ''}
            value={selectedStaff}
            onChange={(event, newValue) => {
              setSelectedStaff(newValue);
              setError('');
            }}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('tasks:completion_receipt.signing_as', { defaultValue: 'Signing as' })}
                placeholder={t('tasks:completion_receipt.select_staff', { defaultValue: 'Select staff member' })}
                required
                error={!!error && !selectedStaff}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedStaff ? (
                    <InputAdornment position="start">
                      <ProfilePicture
                        profilePictureFileId={selectedStaff?.profile_picture_file_id}
                        name={selectedStaff?.name}
                        size={24}
                        imageSize="32x32"
                        bgcolor="grey.400"
                        color={selectedStaff?.color}
                      />
                    </InputAdornment>
                  ) : params.InputProps.startAdornment,
                }}
              />
            )}
            renderOption={({ key, ...props }, option) => (
              <Box key={key} component="li" {...props}>
                <ProfilePicture
                  profilePictureFileId={option?.profile_picture_file_id}
                  name={option?.name}
                  size={42}
                  imageSize="64x64"
                  bgcolor="grey.400"
                  color={option?.color}
                  sx={{ mr: 3 }}
                />
                <Typography variant="body" fontSize="16px">{option.name}</Typography>
              </Box>
            )}
          />

          {/* PIN or password; PIN is offered to everyone but only usable once they've set one up */}
          {selectedStaff && (
            <Box>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeChange}
                disabled={loading}
                fullWidth
              >
                <ToggleButton value="pin" disabled={!pinAvailable}>
                  <Dialpad fontSize="small" sx={{ mr: 1 }} />
                  {t('auth:password_dialog.use_pin', { defaultValue: 'Use PIN' })}
                </ToggleButton>
                <ToggleButton value="password">
                  <Lock fontSize="small" sx={{ mr: 1 }} />
                  {t('auth:password_dialog.use_password', { defaultValue: 'Use Password' })}
                </ToggleButton>
              </ToggleButtonGroup>
              {!pinAvailable && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                  {t('tasks:completion_receipt.no_pin_hint', { name: selectedStaff.name })}
                </Typography>
              )}
            </Box>
          )}

          {/* Credentials only once it's clear whose they are */}
          {!selectedStaff ? null : mode === 'pin' ? (
            <Box sx={{ py: 1 }}>
              <PinKeypad
                value={pin}
                onChange={setPin}
                disabled={loading}
                error={!!error && pin.length === 0}
                autoCapture
                onEnter={handleSubmit}
              />
            </Box>
          ) : (
            <TextField
              label={t('common:labels.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter' && !loading) handleSubmit(); }}
              required
              fullWidth
              autoFocus={mode === 'password'}
              disabled={loading}
              helperText={t('tasks:completion_receipt.password_verify_help', { defaultValue: 'Enter your password to verify your identity' })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <TextField
            label={t('tasks:completion_receipt.signature_notes', { defaultValue: 'Notes (Optional)' })}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={loading}
            placeholder={t('tasks:completion_receipt.notes_placeholder', { defaultValue: 'Add any notes or observations about the task completion...' })}
            inputProps={{ maxLength: 500 }}
            helperText={`${notes.length}/500`}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || (mode === 'pin' ? pin.length !== 4 : !password) || !selectedStaff}
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={20} /> : <HowToRegIcon />}
        >
          {loading
            ? t('tasks:completion_receipt.signing', { defaultValue: 'Signing...' })
            : t('tasks:completion_receipt.sign_receipt', { defaultValue: 'Sign Receipt' })
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}
